import { Modal, Stack, Text, Group, Button, Alert } from '@mantine/core';
import { AlertTriangle, Clock } from 'react-feather';
import type { CohortCoachWithDetails, SlotConflict } from '../../../services/api/cohortCoaches';
import type { GroupWithMembers } from '../../../services/api/groups';
import type { CoachAssignmentWarning } from './groups/coachConflictUtils';

interface DragDropConflictModalProps {
  opened: boolean;
  onClose: () => void;
  coach: CohortCoachWithDetails | null;
  group: GroupWithMembers | null;
  warnings: CoachAssignmentWarning[];
  isReplacing: boolean;
  onConfirm: () => void;
  onViewConflicts?: (conflicts: SlotConflict[]) => void;
}

export function DragDropConflictModal({
  opened,
  onClose,
  coach,
  group,
  warnings,
  isReplacing,
  onConfirm,
  onViewConflicts,
}: DragDropConflictModalProps) {
  if (!coach || !group) return null;

  const coachName = `${coach.first_name || ''} ${coach.last_name || ''}`.trim() || coach.email;
  const schedulingWarning = warnings.find((w) => w.type === 'scheduling');
  const interestWarning = warnings.find((w) => w.type === 'interest_limit');
  const hasWarnings = warnings.length > 0 || isReplacing;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Assign Coach to Group"
      size="sm"
    >
      <Stack gap="md">
        <Text size="sm">
          Assign <strong>{coachName}</strong> to <strong>{group.name}</strong>?
        </Text>

        {/* Replace existing coach warning */}
        {isReplacing && group.coach_name && (
          <Alert
            icon={<AlertTriangle size={16} />}
            color="yellow"
            variant="light"
            title="Replace Existing Coach"
          >
            <Text size="sm">
              This will replace <strong>{group.coach_name}</strong> as the coach for {group.name}.
            </Text>
          </Alert>
        )}

        {/* Scheduling conflicts */}
        {schedulingWarning && schedulingWarning.type === 'scheduling' && (
          <Alert
            icon={<Clock size={16} />}
            color="yellow"
            variant="light"
            title="Scheduling Conflicts"
          >
            <Group gap="xs" justify="space-between">
              <Text size="sm">
                {schedulingWarning.conflicts.length} conflict{schedulingWarning.conflicts.length !== 1 ? 's' : ''} with {group.name} sessions
              </Text>
              {onViewConflicts && (
                <Button
                  variant="subtle"
                  size="compact-xs"
                  color="yellow"
                  onClick={() => onViewConflicts(schedulingWarning.conflicts)}
                >
                  View
                </Button>
              )}
            </Group>
          </Alert>
        )}

        {/* Interest limit */}
        {interestWarning && interestWarning.type === 'interest_limit' && (
          <Alert
            icon={<AlertTriangle size={16} />}
            color="yellow"
            variant="light"
            title="Over Interest Limit"
          >
            <Text size="sm">
              This exceeds <strong>{coachName}</strong>&apos;s interest of {interestWarning.maxGroups} group{interestWarning.maxGroups !== 1 ? 's' : ''} (currently assigned to {interestWarning.currentCount}).
            </Text>
          </Alert>
        )}

        <Group justify="flex-end" gap="sm" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onConfirm} color={hasWarnings ? 'yellow' : undefined}>
            {hasWarnings ? 'Assign Anyway' : 'Assign'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
